import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import CustomItemForm from './CustomItemForm';

// Helper function to format price values
function formatPrice(price) {
    const num = typeof price === 'number' ? price : Number(price);
    if (isNaN(num)) return '';

    // Format with 2 decimals, then remove .00 if present
    const formatted = num.toFixed(2);
    return formatted.endsWith('.00') ? formatted.slice(0, -3) : formatted;
}

// Group products by category
function groupByCategory(products, t) {
    const groups = {};
    products.forEach(product => {
        const cat = product.category || t('uncategorized');
        if (!groups[cat]) groups[cat] = [];
        groups[cat].push(product);
    });
    return groups;
}

// OrderProducts: shows a list of products available to order
// onAddCustomItem is passed only while the tenant has custom items on; without
// it no custom item button renders.
function OrderProducts({ products, categories = [], onAddProduct, onAddCustomItem, theme, layoutMode = 'mobile', preorderComponent }) {
    const { t, isSpanish } = useLanguage();
    const [expandedCategory, setExpandedCategory] = useState(null);
    const [autoExpanded, setAutoExpanded] = useState(false);
    const [selectedOptions, setSelectedOptions] = useState({}); // { productId: { groupId: option/[options] } }
    const [showOptions, setShowOptions] = useState({});
    const [addedProductId, setAddedProductId] = useState(null); // Track which product was just added for visual feedback

    // Filter out inactive products before grouping
    const activeProducts = (products || []).filter(product => product.active === 1 || product.active === true);
    const grouped = groupByCategory(activeProducts, t);

    // Build a map from English category name to localized display name
    const catDisplayName = {};
    categories.forEach(cat => {
        catDisplayName[cat.name] = isSpanish ? (cat.name_español || cat.name) : cat.name;
    });

    // Get ordered category names based on categories array order
    // If categories prop is empty, fallback to Object.keys (original behavior)
    const orderedCategoryNames = categories.length > 0
        ? categories.map(cat => cat.name).filter(name => grouped[name])
        : Object.keys(grouped);

    // Auto-expand first category on mount
    useEffect(() => {
        if (!autoExpanded && orderedCategoryNames.length > 0 && expandedCategory === null) {
            setExpandedCategory(orderedCategoryNames[0]);
            setAutoExpanded(true);
        }
    }, [orderedCategoryNames, autoExpanded, expandedCategory]);

    const handleToggle = (category) => {
        setExpandedCategory(prev => prev === category ? null : category);
    };

    // Helper function to get option groups from product
    const getOptionGroups = (product) => {
        // Check for multiple option groups first (new structure for pizzas)
        if (product.option_groups && product.option_groups.length > 0) {
            return product.option_groups;
        }
        // Fallback to single option_group for backwards compatibility
        if (product.option_group) {
            return [product.option_group];
        }
        return [];
    };

    // Helper function to check if product has options
    const hasOptions = (product) => {
        const groups = getOptionGroups(product);
        return groups.length > 0;
    };

    // Helper function to get selected options for a product
    const getSelectedOptionsForProduct = (product) => {
        const groups = getOptionGroups(product);
        if (groups.length === 0) return {};

        const selections = selectedOptions[product.id] || {};
        const result = {};

        groups.forEach(group => {
            // Check if we already have a selection for this group
            if (selections[group.id]) {
                result[group.id] = selections[group.id];
            } else {
                // Initialize with defaults based on group type
                if (group.option_type_id === 1) {
                    // Radio button - select default or first option
                    const defaultOption = group.options.find(opt => opt.selected === 1) || group.options[0];
                    result[group.id] = defaultOption;
                } else if (group.option_type_id === 2) {
                    // Checkbox - select all options with selected = 1
                    result[group.id] = group.options.filter(opt => opt.selected === 1);
                }
            }
        });

        return result;
    };

    // Helper function to calculate price with all option adjustments
    const calculatePriceWithOptions = (product) => {
        const basePrice = Number(product.price) || 0;
        const selections = getSelectedOptionsForProduct(product);
        let totalAdjustment = 0;

        Object.values(selections).forEach(selection => {
            if (Array.isArray(selection)) {
                // Checkbox group - sum all selected options
                selection.forEach(opt => {
                    totalAdjustment += Number(opt.price_adjust) || 0;
                });
            } else if (selection) {
                // Radio group - single option
                totalAdjustment += Number(selection.price_adjust) || 0;
            }
        });

        return basePrice + totalAdjustment;
    };

    // Handle option selection (radio button)
    const handleRadioOptionChange = (productId, groupId, option) => {
        setSelectedOptions(prev => ({
            ...prev,
            [productId]: {
                ...(prev[productId] || {}),
                [groupId]: option
            }
        }));
    };

    // Handle checkbox option toggle
    const handleCheckboxOptionToggle = (productId, groupId, option) => {
        setSelectedOptions(prev => {
            const currentSelections = prev[productId]?.[groupId] || [];
            const isSelected = currentSelections.some(opt => opt.id === option.id);

            let newSelections;
            if (isSelected) {
                // Remove option
                newSelections = currentSelections.filter(opt => opt.id !== option.id);
            } else {
                // Add option
                newSelections = [...currentSelections, option];
            }

            return {
                ...prev,
                [productId]: {
                    ...(prev[productId] || {}),
                    [groupId]: newSelections
                }
            };
        });
    };

    // Handle adding product with selected options
    const handleAddProductWithOption = (product) => {
        const groups = getOptionGroups(product);

        if (groups.length === 0) {
            // No options - add product as-is
            onAddProduct(product);
            // Show success feedback
            setAddedProductId(product.id);
            setTimeout(() => setAddedProductId(null), 500); // Reset after 500ms
            return;
        }

        const selections = getSelectedOptionsForProduct(product);
        const adjustedPrice = calculatePriceWithOptions(product);

        // Build display name with selected options
        let displayName = product.name;
        const optionDescriptions = [];

        groups.forEach(group => {
            const selection = selections[group.id];
            if (Array.isArray(selection) && selection.length > 0) {
                optionDescriptions.push(selection.map(o => o.name).join(', '));
            } else if (selection && group.option_type_id === 1) {
                // Only show radio selection if it's not the default
                const defaultOption = group.options.find(opt => opt.selected === 1);
                if (!defaultOption || selection.id !== defaultOption.id) {
                    optionDescriptions.push(selection.name);
                }
            }
        });

        if (optionDescriptions.length > 0) {
            displayName = `${product.name} (${optionDescriptions.join(' | ')})`;
        }

        // Create product with options
        const productWithOptions = {
            ...product,
            selectedOptions: selections,
            adjustedPrice,
            displayName
        };

        onAddProduct(productWithOptions);

        // Show success feedback
        setAddedProductId(product.id);
        setTimeout(() => setAddedProductId(null), 500); // Reset after 500ms
    };

    // Handle toggle options visibility - only one product can have options expanded at a time
    const handleToggleOptions = (productId) => {
        setShowOptions(prev => {
            const isCurrentlyExpanded = prev[productId];
            // If clicking the already expanded product, collapse it
            if (isCurrentlyExpanded) {
                return { [productId]: false };
            }
            // Otherwise, collapse all others and expand this one
            return { [productId]: true };
        });
    };

    // Render desktop layout: separate categories and products
    const renderProductList = (items) => (
        <ul style={{
            listStyle: 'none',
            padding: 0,
            margin: 0,
            width: '100%',
        }}>
            {items.map(product => {
                const productHasOptions = hasOptions(product);
                const displayPrice = productHasOptions ? calculatePriceWithOptions(product) : Number(product.price);
                const optionGroups = getOptionGroups(product);
                const selections = getSelectedOptionsForProduct(product);

                return (
                    <li
                        key={product.id}
                        style={{
                            width: '100%',
                            marginBottom: '0.5rem',
                            background: theme?.colors?.background,
                            borderRadius: theme?.borderRadius || 6,
                            boxShadow: theme?.boxShadow,
                            padding: '0.3rem 0.5rem',
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: productHasOptions ? '0rem' : '0' }}>
                            <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: 0 }}>
                                <span style={{ color: theme?.colors?.primary, fontWeight: 'bold', fontSize: '0.95rem', flexShrink: 0 }}>
                                    ${formatPrice(displayPrice)}
                                </span>
                                <strong style={{ fontSize: '0.95rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{product.name}</strong>
                            </div>

                            {productHasOptions && (
                                <button
                                    style={{
                                        padding: '0.65rem 0.8rem',
                                        background: showOptions[product.id] ? theme?.colors?.primary : theme?.colors?.secondary,
                                        color: showOptions[product.id] ? theme?.colors?.surface : theme?.colors?.text,
                                        border: 'none',
                                        borderRadius: theme?.borderRadius || 6,
                                        cursor: 'pointer',
                                        fontWeight: 'bold',
                                        fontSize: '0.85rem',
                                        boxShadow: theme?.boxShadow,
                                        flexShrink: 0,
                                    }}
                                    onClick={() => handleToggleOptions(product.id)}
                                >
                                    {showOptions[product.id] ? t('hideOptions') : t('options')}
                                </button>
                            )}

                            <button
                                style={{
                                    padding: '0.65rem 0.8rem',
                                    background: addedProductId === product.id
                                        ? theme?.colors?.success || '#4caf50'
                                        : theme?.colors?.primary || '#1976d2',
                                    color: theme?.colors?.surface || '#fff',
                                    border: 'none',
                                    borderRadius: theme?.borderRadius || 6,
                                    cursor: 'pointer',
                                    fontWeight: 'bold',
                                    fontSize: '0.85rem',
                                    boxShadow: theme?.boxShadow,
                                    transition: 'background 0.3s ease',
                                    flexShrink: 0,
                                }}
                                onClick={() => handleAddProductWithOption(product)}
                            >
                                {t('add')}
                            </button>
                        </div>

                        {/* Product options - Multiple groups support */}
                        {showOptions[product.id] && productHasOptions && (
                            <div style={{ marginTop: '1rem' }}>
                                {optionGroups.map(group => {
                                    const isRadio = group.option_type_id === 1;
                                    const isCheckbox = group.option_type_id === 2;

                                    return (
                                        <div key={group.id} style={{ marginBottom: '1rem' }}>
                                            <div style={{
                                                fontSize: '0.9rem',
                                                fontWeight: 'bold',
                                                marginBottom: '0.5rem',
                                                color: theme?.colors?.text
                                            }}>
                                                {group.name}{group.mandatory ? ' *' : ''}:
                                            </div>

                                            {isRadio && (
                                                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                                                    {group.options.map(option => {
                                                        const isSelected = selections[group.id]?.id === option.id;
                                                        const priceText = option.price_adjust > 0
                                                            ? ` (+$${formatPrice(option.price_adjust)})`
                                                            : '';

                                                        return (
                                                            <button
                                                                key={option.id}
                                                                style={{
                                                                    padding: '0.375rem 0.75rem',
                                                                    background: isSelected ? theme?.colors?.primary : theme?.colors?.surface,
                                                                    color: isSelected ? theme?.colors?.surface : theme?.colors?.text,
                                                                    border: `1px solid ${theme?.colors?.primary}`,
                                                                    borderRadius: theme?.borderRadius || 4,
                                                                    cursor: 'pointer',
                                                                    fontSize: '0.8rem',
                                                                    fontWeight: isSelected ? 'bold' : 'normal',
                                                                    boxShadow: isSelected ? theme?.boxShadow : 'none',
                                                                    transition: 'all 0.2s',
                                                                }}
                                                                onClick={() => handleRadioOptionChange(product.id, group.id, option)}
                                                            >
                                                                {option.name}{priceText}
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            )}

                                            {isCheckbox && (
                                                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                                                    {group.options.map(option => {
                                                        const isSelected = selections[group.id]?.some(opt => opt.id === option.id) || false;
                                                        const priceText = option.price_adjust > 0
                                                            ? ` (+$${formatPrice(option.price_adjust)})`
                                                            : '';

                                                        return (
                                                            <button
                                                                key={option.id}
                                                                style={{
                                                                    padding: '0.375rem 0.75rem',
                                                                    background: isSelected ? theme?.colors?.primary : theme?.colors?.surface,
                                                                    color: isSelected ? theme?.colors?.surface : theme?.colors?.text,
                                                                    border: `1px solid ${theme?.colors?.primary}`,
                                                                    borderRadius: theme?.borderRadius || 4,
                                                                    cursor: 'pointer',
                                                                    fontSize: '0.8rem',
                                                                    fontWeight: isSelected ? 'bold' : 'normal',
                                                                    boxShadow: isSelected ? theme?.boxShadow : 'none',
                                                                    transition: 'all 0.2s',
                                                                }}
                                                                onClick={() => handleCheckboxOptionToggle(product.id, group.id, option)}
                                                            >
                                                                {isSelected ? '✓ ' : ''}{option.name}{priceText}
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </li>
                );
            })}
        </ul>
    );

    // Product list for a category, followed by the custom item button when
    // the feature is on. The custom line carries the category it was opened from.
    const renderCategoryBody = (category) => {
        const categoryId = categories.find(cat => cat.name === category)?.id
            ?? grouped[category]?.[0]?.category_id;
        return (
            <>
                {renderProductList(grouped[category])}
                {onAddCustomItem && categoryId && (
                    <CustomItemForm
                        key={category}
                        theme={theme}
                        categoryId={categoryId}
                        onAdd={onAddCustomItem}
                    />
                )}
            </>
        );
    };

    // Main render
    // For desktop mode, render all components in a single wrapper to maintain grid structure
    if (layoutMode === 'desktop') {
        return (
            <>
                <div className="order-categories" style={{ padding: '1rem', background: theme?.colors?.surface, color: theme?.colors?.text, borderRadius: theme?.borderRadius || 8, boxShadow: theme?.boxShadow }}>
                    {orderedCategoryNames.map((category) => (
                        <button
                            key={category}
                            style={{
                                width: '100%',
                                textAlign: 'left',
                                padding: '0.75rem 1rem',
                                background: expandedCategory === category ? theme?.colors?.primary : theme?.colors?.secondary,
                                color: theme?.colors?.surface || '#fff',
                                border: 'none',
                                borderRadius: theme?.borderRadius || 6,
                                fontWeight: 'bold',
                                fontSize: '1.05rem',
                                cursor: 'pointer',
                                boxShadow: theme?.boxShadow,
                                marginBottom: '0.5rem',
                                transition: 'background 0.2s',
                            }}
                            onClick={() => handleToggle(category)}
                        >
                            {catDisplayName[category] || category}
                        </button>
                    ))}
                </div>
                <div className="order-products-wrapper">
                    <div style={{ padding: '1rem', background: theme?.colors?.surface, color: theme?.colors?.text, borderRadius: theme?.borderRadius || 8, boxShadow: theme?.boxShadow, marginBottom: '1rem' }}>
                        {expandedCategory && grouped[expandedCategory] ? (
                            renderCategoryBody(expandedCategory)
                        ) : (
                            <div style={{ padding: '2rem', textAlign: 'center', color: theme?.colors?.textSecondary }}>
                                {t('selectCategory')}
                            </div>
                        )}
                    </div>
                    {preorderComponent}
                </div>
            </>
        );
    }

    return (
        // Mobile/Tablet: Grid layout with categories
        <div style={{ padding: '1rem', background: theme?.colors?.surface, color: theme?.colors?.text, width: '100%', boxSizing: 'border-box', overflow: 'hidden', marginBottom: 0 }}>
            <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr',
                gap: '1rem',
                marginBottom: 0,
                minWidth: 0
            }}>
                {orderedCategoryNames.map((category) => (
                    <React.Fragment key={category}>
                        <button
                            style={{
                                width: '100%',
                                minWidth: 0,
                                textAlign: 'center',
                                padding: '0.75rem 1rem',
                                background: expandedCategory === category ? theme?.colors?.primary : theme?.colors?.secondary,
                                color: theme?.colors?.surface,
                                border: expandedCategory === category ? `2px solid ${theme?.colors?.primary}` : 'none',
                                borderRadius: theme?.borderRadius || 6,
                                fontWeight: 'bold',
                                fontSize: '1.1rem',
                                cursor: 'pointer',
                                boxShadow: theme?.boxShadow,
                                transition: 'background 0.2s, border 0.2s',
                            }}
                            onClick={() => handleToggle(category)}
                        >
                            {catDisplayName[category] || category}
                        </button>
                        {/* If this is the expanded category, show products in a full-width row below */}
                        {expandedCategory === category && (
                            <div style={{ gridColumn: '1', width: '100%' }}>
                                {renderCategoryBody(category)}
                            </div>
                        )}
                    </React.Fragment>
                ))}
                {/* Fill last cell if odd number of categories */}
                {/* No filler needed for single-column layout */}
            </div>
        </div>
    );
}

export default OrderProducts

